import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Ban, Banknote, Check, Copy, ExternalLink, FileX, HandCoins, QrCode, Repeat, Send, Undo2 } from 'lucide-react';
import { Botao, Selo } from '../../componentes/base';
import { Chatter } from '../../componentes/chatter';
import { Carregando, Erro } from '../../componentes/estados';
import { Confirmar, Drawer, Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { ErroRequisicao, get, mensagemDeErro, post, textoDeErroGravado } from '../../nucleo/api';
import { data, dataHora, dinheiro, textoLivre } from '../../nucleo/formato';
import { descricaoGerada } from '../../nucleo/texto-sistema';
import { rotuloOrigem } from '../../nucleo/rotulos';
import { useSessao } from '../../nucleo/sessao';
import { useCentrosFin, useVerFinanceiro } from './dados';
import { EstornarPerda } from './EstornarPerda';
import { EstornarBaixa } from './EstornarBaixa';
import { EnviarCobranca } from './EnviarCobranca';
import { ModalBaixa } from './ModalBaixa';
import { Renegociar } from './Renegociar';
import { AplicarCredito } from './AplicarCredito';
import { DesfazerAplicacao } from './DesfazerAplicacao';
import { useTextoDoProvedor } from './provedor-texto';
import { COR_ESTADO, FORMAS_COBRANCA, hojeLocal, invalidarContas, pixSicrediVencido, rotuloEtapaEnvio, type AplicacaoCredito, type Baixa, type CobrancaDoc, type ConfigAsaas, type DocumentoDetalhe, type FormaCobranca } from './tipos';
import { TextoCortado } from '../../componentes/texto-cortado';
import { anunciarOcultos } from '../../componentes/sensivel';
import { AreaSensivel, CampoData } from '../../componentes/formulario';
import { LinkParceiro } from '../../componentes/link-parceiro';
import { rotuloNomeForma } from '../../nucleo/forma-pagamento';

/** Percentual do rateio sem zeros à direita (até 4 casas). */
const numeroPct = (p: number) => String(Math.round(p * 1e4) / 1e4);

function Linha({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  const { t } = useTranslation();
  const vazio = children === '' || children === null || children === undefined || children === false;
  return (
    <div className="flex min-w-0 justify-between gap-4 py-1.5 text-sm">
      <dt className="shrink-0 text-tinta-3">{rotulo}</dt>
      <TextoCortado como="dd" className={`min-w-0 text-right ${vazio ? 'text-tinta-3' : ''}`}>{vazio ? t('comum.naoInformado') : children}</TextoCortado>
    </div>
  );
}

function Secao({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <section className="mt-6 min-w-0">
      <h3 className="kicker mb-2">{titulo}</h3>
      {children}
    </section>
  );
}

function Copiavel({ rotulo, valor }: { rotulo: string; valor: string }) {
  const { t } = useTranslation();
  const [copiado, setCopiado] = useState(false);
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <span className="text-sm font-medium text-tinta-2">{rotulo}</span>
      <div className="flex min-w-0 items-center gap-2">
        <TextoCortado como="code" className="numero min-w-0 flex-1 rounded-lg border border-borda bg-sup-2 px-3 py-2 text-xs text-tinta-1">{valor}</TextoCortado>
        <Botao
          aria-label={t('comum.copiar')}
          title={t('comum.copiar')}
          onClick={() => {
            void navigator.clipboard?.writeText(valor).then(() => {
              setCopiado(true);
              setTimeout(() => setCopiado(false), 2000);
            });
          }}
          icone={copiado ? <Check className="size-4" /> : <Copy className="size-4" />}
        />
      </div>
    </div>
  );
}

/** Ficha do documento (M5.7): dados, itens, baixas, cobrança por Pix e boleto, régua e atividade. */
export function DetalheDocumento({ id, aoFechar }: { id: string | null; aoFechar: () => void }) {
  const { t } = useTranslation('contas');
  const { t: tn } = useTranslation();
  const tp = useTextoDoProvedor();
  const s = useSessao();
  const toast = useToast();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['/contas/documentos', 'obter', id],
    queryFn: () => get<DocumentoDetalhe>(`/contas/documentos/${id}`),
    enabled: !!id,
    // Enquanto o Asaas gera a cobrança, a ficha se atualiza sozinha.
    refetchInterval: (consulta) => (consulta.state.data?.cobranca?.estado === 'gerando' ? 3000 : false),
  });
  const [baixando, setBaixando] = useState(false);
  const [cancelando, setCancelando] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [cobrando, setCobrando] = useState(false);
  const [forma, setForma] = useState<FormaCobranca | null>(null);
  const [semConector, setSemConector] = useState(false);
  const [perdendo, setPerdendo] = useState(false);
  const [dataPerda, setDataPerda] = useState(hojeLocal());
  const [motivoPerda, setMotivoPerda] = useState('');
  const [chavePerda] = useState(() => crypto.randomUUID());
  const [erroPerda, setErroPerda] = useState<string | null>(null);
  // Fase 2 (W14): estorno da perda e renegociação deste documento.
  const [estornandoPerda, setEstornandoPerda] = useState(false);
  // Fase 2 (W24, K9, F4): a baixa normal (com lançamento) que está sendo estornada pela ficha.
  const [estornandoBaixa, setEstornandoBaixa] = useState<Baixa | null>(null);
  const [renegociando, setRenegociando] = useState(false);
  // Fase 2 (W29): "Enviar cobrança agora" (mesma mensagem da régua, pelo canal escolhido).
  const [enviando, setEnviando] = useState(false);
  // Fase 2 (W34): aplicar a nota de crédito em fatura e desfazer uma aplicação (pela nota ou pela fatura).
  const [aplicando, setAplicando] = useState(false);
  const [desfazendo, setDesfazendo] = useState<AplicacaoCredito | null>(null);
  const centros = useCentrosFin();
  const verFinanceiro = useVerFinanceiro();

  const cancelar = useMutation({
    mutationFn: () => post(`/contas/documentos/${id}/cancelar`, { motivo: motivo.trim() || undefined }),
    onSuccess: async () => {
      await invalidarContas(qc);
      setCancelando(false);
      setMotivo('');
      toast.sucesso(t('mensagens.cancelado'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  // Fase 2, W7 (F7): perda de crédito, sem caixa, com motivo obrigatório.
  const perder = useMutation({
    mutationFn: () => post(`/contas/documentos/${id}/perder`, { data: dataPerda, motivo: motivoPerda.trim() }, { 'Idempotency-Key': chavePerda }),
    onSuccess: async () => {
      await invalidarContas(qc);
      setPerdendo(false);
      setMotivoPerda('');
      toast.sucesso(t('w7.perdaRegistrada'));
    },
    onError: (e) => setErroPerda(mensagemDeErro(e)),
  });
  const gerar = useMutation({
    mutationFn: () => post(`/contas/documentos/${id}/cobranca`, { forma: formaEscolhida }),
    onSuccess: async () => {
      await invalidarContas(qc);
      setCobrando(false);
      setSemConector(false);
      toast.sucesso(tp('mensagens.cobrancaSolicitada', provedorNovo));
    },
    onError: (e) => {
      if (e instanceof ErroRequisicao && e.corpo.code === 'conector.desconectado') setSemConector(true);
      toast.erro(mensagemDeErro(e));
      setCobrando(false);
    },
  });

  const d = q.data;
  const aberto = !!d && (d.estado === 'em_aberto' || d.estado === 'parcial');
  const receber = d?.direcao === 'receber';
  const podeBaixar = aberto && s.pode('contas.baixar');
  const podeCancelar = !!d && d.estado === 'em_aberto' && s.pode('contas.cancelar');
  const podePerder = !!d && d.tipo === 'fatura' && aberto && s.pode('contas.perder');
  const podeEstornarPerda = !!d && d.estado === 'perdido' && s.pode('contas.perder');
  // Fase 2 (W24, K9, F4): estorno da baixa com a permissão de baixar; renegociado e cancelado não reabrem.
  const podeEstornarBaixa = (b: Baixa) => !!d && s.pode('contas.baixar') && !b.estornada_em && (b.tipo ?? 'normal') === 'normal' && !!b.lancamento_id && d.estado !== 'renegociado' && d.estado !== 'cancelado';
  const podeRenegociar = !!d && aberto && (d.tipo === 'fatura' || d.tipo === 'conta_pagar') && s.pode('contas.cancelar') && s.pode('contas.criar');
  const nomeCentro = (id: string) => centros.data?.itens.find((c) => c.id === id)?.nome ?? id.slice(0, 8);
  const podeCobrar = !!d && d.tipo === 'fatura' && aberto && s.pode('contas.cobrar') && s.instalado('integracoes');
  const cob: CobrancaDoc | null = d?.cobranca ?? null;
  // Fase 2 (W28): a forma sugerida no "Gerar cobrança" é a da configuração do Asaas (Contas > Cobranças).
  const cfgAsaas = useQuery({ queryKey: ['/contas/asaas'], queryFn: () => get<ConfigAsaas>('/contas/asaas'), enabled: podeCobrar && cobrando, staleTime: 30_000 });
  const formaEscolhida: FormaCobranca = forma ?? cfgAsaas.data?.forma ?? 'pix';
  // Antes da r51 (classe do R50-VIS-M1): o texto que nomeia o banco escolhe pelo provedor da cobrança que existe (ausente =
  // Asaas) ou, para a que ainda vai ser gerada, pelo da regra; sem nenhum dos dois, o texto neutro.
  const provedorCob = cob ? (cob.provedor ?? 'asaas') : null;
  const provedorNovo = d?.provedor_regra ?? null;
  const provedorEnvio = cob && cob.estado !== 'erro' && cob.estado !== 'cancelada' ? provedorCob : provedorNovo;
  // Review r51 (VIS-B2, SPEC-B2): com a regra "nenhum provedor", a fatura não ganha cobrança (o POST recusa com
  // `cobranca.sem_provedor`): a ficha não oferece "Gerar cobrança" nem o "Gerar cobrança antes" do envio, e diz por quê.
  const regraNenhum = provedorNovo === 'nenhum';
  // Review r46 (ROB-M1, SPEC-M3): o Pix do Sicredi vencido sem pagamento libera gerar outro (o agendado também troca sozinho).
  const pixVencido = !!d && pixSicrediVencido(cob, d.vencimento, hojeLocal());
  const podeGerarDeNovo = !cob || cob.estado === 'erro' || cob.estado === 'cancelada' || cob.estado === 'agendada' || pixVencido;
  const podeEnviarAgora = !!d && d.tipo === 'fatura' && d.direcao === 'receber' && aberto && s.pode('contas.cobrar');
  // Fase 2 (W34): na nota de crédito em aberto, "Aplicar em fatura" é o primário (a devolução em dinheiro fica secundária).
  const podeAplicar = !!d && d.tipo === 'nota_credito' && aberto && s.pode('contas.baixar');
  const aplicacoes = d?.aplicacoes ?? [];
  const mostrarAplicacoes = !!d && (d.tipo === 'nota_credito' || aplicacoes.length > 0);

  return (
    <>
      <Drawer
        aberto={!!id}
        aoFechar={aoFechar}
        largura="lg"
        titulo={d ? `${t(`tipo.${d.tipo}`)} ${d.numero}` : t('detalhe.titulo')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={aoFechar}>
              {tn('comum.fechar')}
            </Botao>
            {podeCancelar && (
              <Botao variante="perigo" icone={<Ban className="size-4" />} onClick={() => setCancelando(true)}>
                {t('acoes.cancelar')}
              </Botao>
            )}
            {podePerder && (
              <Botao icone={<FileX className="size-4" />} onClick={() => (setErroPerda(null), setPerdendo(true))}>
                {t('w7.perder')}
              </Botao>
            )}
            {podeRenegociar && (
              <Botao icone={<Repeat className="size-4" />} onClick={() => setRenegociando(true)}>
                {t('w14.renegociar')}
              </Botao>
            )}
            {podeEstornarPerda && (
              <Botao icone={<Undo2 className="size-4" />} onClick={() => setEstornandoPerda(true)}>
                {t('w14.estornarPerda')}
              </Botao>
            )}
            {d && podeBaixar && (
              <Botao variante={podeAplicar ? 'secundario' : 'primario'} icone={<Banknote className="size-4" />} onClick={() => setBaixando(true)}>
                {receber ? t('acoes.baixarReceber') : t('acoes.baixarPagar')}
              </Botao>
            )}
            {podeAplicar && (
              <Botao variante="primario" icone={<HandCoins className="size-4" />} onClick={() => setAplicando(true)}>
                {t('w34.aplicar')}
              </Botao>
            )}
          </>
        }
      >
        {q.isPending && !!id && <Carregando />}
        {q.isError && <Erro erro={q.error} aoTentar={() => q.refetch()} />}
        {d && (
          <>
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Selo cor={d.direcao === 'receber' ? 'verde' : 'perigo'}>{t(`direcao.${d.direcao}`)}</Selo>
              <Selo cor={COR_ESTADO[d.estado]}>{t(`estado.${d.estado}`)}</Selo>
              {d.atrasado && <Selo cor="laranja">{t('estado.atrasadoDias', { count: d.dias_atraso })}</Selo>}
              {d.parcelas > 1 && <Selo>{t('detalhe.parcelaDe', { n: d.parcela, total: d.parcelas })}</Selo>}
            </div>
            <p className={`numero mb-1 text-3xl font-semibold ${aberto ? (d.atrasado ? 'text-laranja' : 'text-tinta-1') : 'text-tinta-2'}`}>{dinheiro(aberto ? d.saldo_centavos : d.valor_centavos - d.desconto_centavos, d.moeda)}</p>
            <p className="mb-4 text-sm text-tinta-3">
              {aberto ? t('detalhe.saldoEmAberto') : d.estado === 'pago' ? t('detalhe.pagoEm', { data: data(d.pago_em) }) : d.estado === 'perdido' ? t('w7.perdidoEm', { data: data(d.perdido_em ?? null) }) : d.estado === 'renegociado' ? t('w14.renegociadoEm', { data: data(d.renegociado_em ?? null) }) : t('detalhe.valorDocumento')}
            </p>

            <dl className="divide-y divide-borda">
              {/* Fase 2 (W34): a nota de crédito é do cliente (compensa fatura dele): "Parceiro", como na lista, não "Fornecedor". */}
              <Linha rotulo={d.direcao === 'receber' ? t('campos.cliente') : d.tipo === 'nota_credito' ? t('campos.parceiro') : t('campos.fornecedor')}>
                <LinkParceiro id={d.parceiro_id} nome={d.parceiro_nome} />
              </Linha>
              <Linha rotulo={t('campos.emissao')}>{data(d.emissao)}</Linha>
              {d.competencia && d.competencia !== d.emissao && <Linha rotulo={t('w7.competencia')}>{data(d.competencia)}</Linha>}
              {d.centro_custo_nome && <Linha rotulo={t('w7.centroCusto')}>{d.centro_custo_nome}</Linha>}
              {d.forma_pagamento_nome && <Linha rotulo={t('w24.formaPagamento')}>{rotuloNomeForma(d.forma_pagamento_nome)}</Linha>}
              {/* Review r28 spec (B2, varredura): sem `financeiro.ver` não há nome de centro; o rateio some (antes, o começo do id). */}
              {!!d.rateio?.length && verFinanceiro && <Linha rotulo={t('w14.rateio')}>{d.rateio.map((r) => `${nomeCentro(r.centro_custo_id)} ${numeroPct(r.percentual)}%`).join(', ')}</Linha>}
              {d.renegociacao_origem_id && <Linha rotulo={t('campos.origem')}>{t('w14.parcelaDeRenegociacao')}</Linha>}
              {d.estado === 'perdido' && d.motivo_perda && <Linha rotulo={t('w7.perdaMotivo')}>{textoLivre(d.motivo_perda, d.parceiro_nome)}</Linha>}
              {d.chave_externa && (
                <Linha rotulo={t('w7.chaveExterna')}>
                  <span className="numero">{d.chave_externa}</span>
                </Linha>
              )}
              <Linha rotulo={t('campos.vencimento')}>
                <span className={d.atrasado ? 'font-medium text-laranja' : undefined}>{data(d.vencimento)}</span>
              </Linha>
              <Linha rotulo={t('campos.condicao')}>{d.condicao_pagamento === 'avista' ? t('detalhe.avista') : d.condicao_pagamento}</Linha>
              <Linha rotulo={t('campos.valor')}>{anunciarOcultos(dinheiro(d.valor_centavos, d.moeda))}</Linha>
              {d.desconto_centavos > 0 && <Linha rotulo={t('campos.desconto')}>{anunciarOcultos(dinheiro(d.desconto_centavos, d.moeda))}</Linha>}
              {(d.juros_mes_pct > 0 || d.multa_pct > 0) && (
                <Linha rotulo={t('campos.encargos')}>{t('detalhe.encargosTaxas', { juros: d.juros_mes_pct, multa: d.multa_pct })}</Linha>
              )}
              {d.descricao && <Linha rotulo={t('campos.descricao')}>{textoLivre(descricaoGerada(d.descricao), d.parceiro_nome)}</Linha>}
              {d.origem && (
                <Linha rotulo={t('campos.origem')}>
                  {rotuloOrigem(d.origem)}
                </Linha>
              )}
            </dl>

            {d.atrasado && d.encargos_hoje.total_centavos > 0 && (
              <div className="mt-4 rounded-lg border border-laranja px-3 py-2 text-sm">
                <p className="font-medium text-laranja">{t('detalhe.emAtraso', { count: d.dias_atraso })}</p>
                <p className="mt-1 text-tinta-2">
                  {t('detalhe.encargosHoje', { juros: dinheiro(d.encargos_hoje.juros_centavos, d.moeda), multa: dinheiro(d.encargos_hoje.multa_centavos, d.moeda), total: dinheiro(d.encargos_hoje.total_centavos, d.moeda) })}
                </p>
              </div>
            )}

            <Secao titulo={t('detalhe.itens')}>
              <ul className="divide-y divide-borda rounded-lg border border-borda">
                {d.itens.map((i, n) => (
                  <li key={n} className="flex min-w-0 items-start justify-between gap-3 px-3 py-2 text-sm">
                    <span className="min-w-0">
                      <span className="block truncate" title={textoLivre(i.descricao, d.parceiro_nome)}>{textoLivre(i.descricao, d.parceiro_nome)}</span>
                      <TextoCortado className="block text-xs text-tinta-3">
                        {i.quantidade} x {anunciarOcultos(dinheiro(i.valor_unitario_centavos, d.moeda))}
                        {i.categoria_nome ? ` · ${i.categoria_nome}` : ''}
                      </TextoCortado>
                    </span>
                    <span className="numero shrink-0">{anunciarOcultos(dinheiro(i.valor_centavos, d.moeda))}</span>
                  </li>
                ))}
              </ul>
            </Secao>

            <Secao titulo={t('detalhe.baixas')}>
              {d.baixas.length === 0 ? (
                <p className="text-sm text-tinta-3">{t('detalhe.semBaixas')}</p>
              ) : (
                <ul className="divide-y divide-borda rounded-lg border border-borda">
                  {d.baixas.map((b) => (
                    <li key={b.id} className="flex min-w-0 flex-col gap-1 px-3 py-2 text-sm">
                      <span className="flex min-w-0 items-center justify-between gap-3">
                        <span className="numero">{data(b.data)}</span>
                        <span className={`numero font-medium${b.estornada_em ? ' text-tinta-3 line-through' : ''}`}>{dinheiro(b.tipo === 'perda' || b.tipo === 'renegociacao' ? b.valor_centavos : b.total_centavos, b.moeda)}</span>
                      </span>
                      <span className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-xs text-tinta-3">
                        <span>{t('detalhe.principal', { valor: dinheiro(b.valor_centavos, b.moeda) })}</span>
                        {b.juros_centavos > 0 && <span>{t('detalhe.juros', { valor: dinheiro(b.juros_centavos, b.moeda) })}</span>}
                        {b.multa_centavos > 0 && <span>{t('detalhe.multa', { valor: dinheiro(b.multa_centavos, b.moeda) })}</span>}
                        {b.desconto_centavos > 0 && <span>{t('detalhe.descontoBaixa', { valor: dinheiro(b.desconto_centavos, b.moeda) })}</span>}
                        {(b.tarifa_centavos ?? 0) > 0 && <span>{t('w7.tarifa', { valor: dinheiro(b.tarifa_centavos ?? 0, b.moeda) })}</span>}
                        {b.tipo === 'perda' && <Selo cor="perigo">{t('w7.baixaPerda')}</Selo>}
                        {b.tipo === 'renegociacao' && <Selo cor="roxo">{t('w14.baixaRenegociacao')}</Selo>}
                        {b.conta_nome && <span className="truncate" title={b.conta_nome}>{b.conta_nome}</span>}
                        {b.forma_pagamento_nome && <span className="truncate" title={rotuloNomeForma(b.forma_pagamento_nome)}>{rotuloNomeForma(b.forma_pagamento_nome)}</span>}
                        <Selo cor={b.origem === 'asaas' || b.origem === 'sicredi' ? 'ciano' : 'neutro'}>{t(`origemBaixa.${b.origem}`)}</Selo>
                        {b.estornada_em && <Selo cor="laranja">{t('detalhe.baixaEstornada')}</Selo>}
                        {podeEstornarBaixa(b) && (
                          <Botao
                            tamanho="sm"
                            variante="fantasma"
                            className="ml-auto"
                            icone={<Undo2 className="size-4" />}
                            aria-label={t('w24.estornarDe', { valor: dinheiro(b.total_centavos, b.moeda), data: data(b.data) })}
                            onClick={() => setEstornandoBaixa(b)}
                          >
                            {t('w24.estornar')}
                          </Botao>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Secao>

            {mostrarAplicacoes && (
              <Secao titulo={t('w34.secao')}>
                {aplicacoes.length === 0 ? (
                  <p className="text-sm text-tinta-3">{t('w34.semAplicacoes')}</p>
                ) : (
                  <ul className="divide-y divide-borda rounded-lg border border-borda" data-aplicacoes>
                    {aplicacoes.map((a) => {
                      const daNota = d.tipo === 'nota_credito';
                      return (
                        <li key={a.id} className="flex min-w-0 flex-col gap-1 px-3 py-2 text-sm" data-aplicacao={a.id}>
                          <span className="flex min-w-0 items-center justify-between gap-3">
                            <TextoCortado className="numero min-w-0">
                              {data(a.data)} · {daNota ? t('w34.naFatura', { numero: a.fatura_numero ?? '' }) : t('w34.daNota', { numero: a.nota_numero ?? '' })}
                            </TextoCortado>
                            <span className={`numero shrink-0 font-medium${a.desfeita_em ? ' text-tinta-3 line-through' : ''}`}>{anunciarOcultos(dinheiro(a.valor_centavos, a.moeda))}</span>
                          </span>
                          <span className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-xs text-tinta-3">
                            <Selo cor="ciano">{t('w34.semCaixaSelo')}</Selo>
                            {a.observacao && <TextoCortado className="min-w-0">{textoLivre(a.observacao, d.parceiro_nome)}</TextoCortado>}
                            {a.desfeita_em && <Selo cor="laranja">{t('w34.desfeitaEm', { data: data(a.desfeita_em) })}</Selo>}
                            {a.desfeita_em && a.motivo_desfazer && <TextoCortado className="min-w-0">{textoLivre(a.motivo_desfazer, d.parceiro_nome)}</TextoCortado>}
                            {!a.desfeita_em && s.pode('contas.baixar') && (
                              <Botao
                                tamanho="sm"
                                variante="fantasma"
                                className="ml-auto"
                                icone={<Undo2 className="size-4" />}
                                aria-label={t('w34.desfazerDe', { valor: dinheiro(a.valor_centavos, a.moeda), data: data(a.data) })}
                                onClick={() => setDesfazendo(a)}
                              >
                                {t('w34.desfazer')}
                              </Botao>
                            )}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </Secao>
            )}

            {d.renegociacao && (
              <Secao titulo={t('w14.renegociacao')}>
                <div className="flex min-w-0 flex-col gap-2 rounded-lg border border-borda px-3 py-2 text-sm" data-renegociacao>
                  <p className="text-tinta-2">
                    {t('w14.renegociadoEm', { data: data(d.renegociacao.data) })} · <span className="numero">{dinheiro(d.renegociacao.total_centavos, d.renegociacao.moeda)}</span>
                  </p>
                  <p className="numero text-xs text-tinta-3">
                    {t('w14.encargosNegociados', { juros: dinheiro(d.renegociacao.juros_centavos, d.renegociacao.moeda), multa: dinheiro(d.renegociacao.multa_centavos, d.renegociacao.moeda), desconto: dinheiro(d.renegociacao.desconto_centavos, d.renegociacao.moeda) })}
                  </p>
                  <TextoCortado className="text-xs text-tinta-3">{textoLivre(d.renegociacao.motivo, d.parceiro_nome)}</TextoCortado>
                  <p className="kicker mt-1">{t('w14.origens')}</p>
                  <p className="numero text-xs text-tinta-2">{d.renegociacao.origens.map((o) => o.numero).join(', ')}</p>
                  <p className="kicker mt-1">{t('w14.novas')}</p>
                  <ul className="divide-y divide-borda">
                    {d.renegociacao.novas.map((n) => (
                      <li key={n.id} className="flex min-w-0 items-center justify-between gap-3 py-1.5">
                        <span className="numero min-w-0 truncate" title={n.numero}>
                          {n.numero} · {data(n.vencimento)}
                        </span>
                        <span className="flex shrink-0 items-center gap-2">
                          <span className="numero">{anunciarOcultos(dinheiro(n.saldo_centavos, d.renegociacao!.moeda))}</span>
                          <Selo cor={COR_ESTADO[n.estado]}>{t(`estado.${n.estado}`)}</Selo>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Secao>
            )}

            {d.tipo === 'fatura' && (
              <Secao titulo={t('cobranca.titulo')}>
                {cob?.estado === 'gerando' && <Carregando texto={t(cob.provedor === 'sicredi' ? 'cobranca.gerandoSicredi' : 'cobranca.gerando')} />}
                {cob?.estado === 'erro' && (
                  <p role="alert" className="mb-3 rounded-lg border border-perigo px-3 py-2 text-sm text-perigo">
                    {t('cobranca.erro', { motivo: textoDeErroGravado(cob.erro) })}
                    {cob.retentar_em && <span className="mt-1 block text-xs text-tinta-2">{tp('cobranca.retentarEm', provedorCob, { quando: dataHora(cob.retentar_em) })}</span>}
                  </p>
                )}
                {cob?.estado === 'agendada' && (
                  <p role="status" className="mb-3 rounded-lg border border-borda px-3 py-2 text-sm text-tinta-2">
                    {t(cob.provedor === 'sicredi' ? 'cobranca.agendadaSicredi' : 'cobranca.agendada', { data: data(cob.agendada_para ?? d.emissao) })}
                    {/* Review r50 (VIS-M1, SPEC-B2): a emissão agendada que falhou mostra o motivo e a hora da falha (r51 ROB-B2: `erro_em`, não a marca do agendado). */}
                    {cob.erro && (
                      <span className="mt-1 block text-xs text-tinta-2">
                        {cob.erro_em ? t('cobranca.agendadaFalhouEm', { motivo: textoDeErroGravado(cob.erro), quando: dataHora(cob.erro_em) }) : t('cobranca.agendadaFalhou', { motivo: textoDeErroGravado(cob.erro) })}
                      </span>
                    )}
                  </p>
                )}
                {cob?.estado === 'cancelada' && (
                  <p role="status" className="mb-3 rounded-lg border border-laranja px-3 py-2 text-sm text-tinta-2">
                    {t(cob.provedor === 'sicredi' ? 'cobranca.removidaNoSicredi' : cob.status === 'DELETED' ? 'cobranca.removidaNoAsaas' : 'cobranca.estornadaNoAsaas')}
                  </p>
                )}
                {cob?.estado === 'gerada' && (
                  <div className="flex min-w-0 flex-col gap-3">
                    <p className="text-sm text-tinta-2">{cob.provedor === 'sicredi' ? t('cobranca.geradaNoSicredi') : t('cobranca.geradaNoAsaas', { forma: t(`cobranca.forma.${cob.forma ?? 'pix'}`) })}</p>
                    {cob.status === 'OVERDUE' && cob.provedor !== 'sicredi' && <p className="text-sm text-laranja">{t('cobranca.vencidaNoAsaas')}</p>}
                    {cob.aviso && <p className="text-xs text-tinta-3">{tp('cobranca.aviso', provedorCob, { motivo: textoDeErroGravado(cob.aviso) })}</p>}
                    {pixVencido && <p className="text-sm text-laranja">{t('cobranca.pixVencidoNoSicredi')}</p>}
                    {cob.pix_copia_e_cola && !pixVencido && <Copiavel rotulo={t('cobranca.pixCopiaECola')} valor={cob.pix_copia_e_cola} />}
                    {cob.linha_digitavel && <Copiavel rotulo={t('cobranca.linhaDigitavel')} valor={cob.linha_digitavel} />}
                    <div className="flex flex-wrap gap-2">
                      {cob.link && (
                        <a href={cob.link} target="_blank" rel="noreferrer" className="inline-flex h-8 items-center gap-2 rounded-lg border border-borda bg-sup-2 px-3 text-sm font-medium hover:bg-sup-3">
                          <ExternalLink className="size-4" aria-hidden />
                          {t('cobranca.abrirFatura')}
                        </a>
                      )}
                      {cob.boleto_url && (
                        <a href={cob.boleto_url} target="_blank" rel="noreferrer" className="inline-flex h-8 items-center gap-2 rounded-lg border border-borda bg-sup-2 px-3 text-sm font-medium hover:bg-sup-3">
                          <ExternalLink className="size-4" aria-hidden />
                          {t('cobranca.abrirBoleto')}
                        </a>
                      )}
                    </div>
                  </div>
                )}
                {podeGerarDeNovo && !podeCobrar && <p className="text-sm text-tinta-3">{aberto ? t('cobranca.indisponivel') : t('cobranca.fechado')}</p>}
                {podeGerarDeNovo && podeCobrar && regraNenhum && (
                  <p className="text-xs text-tinta-3" data-regra-nenhum>
                    {t('cobranca.regraNenhum')}
                  </p>
                )}
                {podeGerarDeNovo && podeCobrar && !regraNenhum && (
                  <div className="flex flex-col items-start gap-2">
                    <Botao icone={<QrCode className="size-4" />} onClick={() => setCobrando(true)}>
                      {cob?.estado === 'erro' ? t('cobranca.tentarDeNovo') : t('cobranca.gerar')}
                    </Botao>
                    <p className="text-xs text-tinta-3">{tp('cobranca.ajuda', provedorNovo)}</p>
                  </div>
                )}
                {podeEnviarAgora && (
                  <div className={`flex flex-col items-start gap-2${cob || podeGerarDeNovo ? ' mt-3' : ''}`} data-enviar-agora>
                    <Botao icone={<Send className="size-4" />} disabled={cob?.estado === 'gerando'} onClick={() => setEnviando(true)}>
                      {t('w29.enviar')}
                    </Botao>
                    <p className="text-xs text-tinta-3">{cob?.estado === 'gerando' ? tp('w29.gerando', provedorCob) : tp('w29.ajuda', provedorEnvio)}</p>
                  </div>
                )}
                {semConector && (
                  <div role="alert" className="mt-3 flex flex-col items-start gap-2 rounded-lg border border-laranja px-3 py-2 text-sm">
                    <p className="text-laranja">{tp('cobranca.semConector', provedorNovo)}</p>
                    <Link to="/integracoes/integracoes/conectores" className="-my-1 inline-block py-1 text-acento-forte hover:underline">
                      {tn('comum.irParaIntegracoes')}
                    </Link>
                  </div>
                )}
              </Secao>
            )}

            {d.envios.length > 0 && (
              <Secao titulo={t('detalhe.envios')}>
                <ul className="divide-y divide-borda rounded-lg border border-borda">
                  {d.envios.map((e) => (
                    <li key={e.id} className="flex min-w-0 items-center justify-between gap-3 px-3 py-2 text-sm">
                      <TextoCortado className="min-w-0">
                        {t(`canal.${e.canal}`)} · {t(...rotuloEtapaEnvio(e))}
                      </TextoCortado>
                      <span className="flex shrink-0 items-center gap-2">
                        <span className="numero text-xs text-tinta-3">{dataHora(e.enviado_em)}</span>
                        <Selo cor={e.resultado === 'enviado' ? 'verde' : e.resultado === 'falhou' ? 'perigo' : 'neutro'}>{t(`resultado.${e.resultado}`)}</Selo>
                      </span>
                    </li>
                  ))}
                </ul>
              </Secao>
            )}

            <div className="mt-6 border-t border-borda pt-4">
              <Chatter entidade="contas.documento" registroId={d.id} nomes={[d.parceiro_nome]} titulo={`${t(`tipo.${d.tipo}`)} ${d.numero}`} rotulosCampos={{ estado: t('campos.estado'), saldo_centavos: t('campos.saldo') }} />
            </div>
          </>
        )}
      </Drawer>

      <ModalBaixa doc={baixando && d ? d : null} aoFechar={() => setBaixando(false)} />
      <EstornarPerda doc={estornandoPerda && d ? d : null} aoFechar={() => setEstornandoPerda(false)} />
      <EstornarBaixa doc={d ?? null} baixa={estornandoBaixa} aoFechar={() => setEstornandoBaixa(null)} />
      <Renegociar docs={renegociando && d ? [d] : null} aoFechar={() => setRenegociando(false)} aoFeito={() => setRenegociando(false)} />
      <AplicarCredito nota={aplicando && d ? d : null} aoFechar={() => setAplicando(false)} />
      <DesfazerAplicacao documentoId={d?.id ?? null} aplicacao={desfazendo} aoFechar={() => setDesfazendo(null)} />
      <EnviarCobranca doc={d ?? null} aberto={enviando} aoFechar={() => setEnviando(false)} aoGerarAntes={podeCobrar && podeGerarDeNovo && !regraNenhum ? () => (setEnviando(false), setCobrando(true)) : undefined} />

      <Modal
        aberto={cancelando}
        aoFechar={() => setCancelando(false)}
        titulo={t('cancelar.titulo')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setCancelando(false)}>
              {tn('comum.voltar')}
            </Botao>
            <Botao variante="perigo" carregando={cancelar.isPending} onClick={() => cancelar.mutate()}>
              {t('acoes.cancelar')}
            </Botao>
          </>
        }
      >
        <p className="mb-3 text-sm text-tinta-2">{tp('cancelar.texto', provedorCob)}</p>
        <label className="flex flex-col gap-1.5 text-sm font-medium text-tinta-2">
          {t('cancelar.motivo')}
          <AreaSensivel rows={3} maxLength={300} value={motivo} onChange={(e) => setMotivo(e.target.value)} className="w-full rounded-lg border border-borda-campo bg-campo px-3 py-2 text-sm text-tinta-1 focus:border-foco focus:outline-2 focus:outline-offset-0 focus:outline-foco" />
        </label>
      </Modal>

      <Modal
        aberto={perdendo && !!d}
        aoFechar={() => setPerdendo(false)}
        titulo={d ? t('w7.perdaTitulo', { numero: d.numero }) : ''}
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setPerdendo(false)}>
              {tn('comum.voltar')}
            </Botao>
            <Botao variante="perigo" disabled={!motivoPerda.trim()} carregando={perder.isPending} onClick={() => (setErroPerda(null), perder.mutate())}>
              {t('w7.perder')}
            </Botao>
          </>
        }
      >
        {d && <p className="mb-3 text-sm text-tinta-2">{t('w7.perdaTexto', { saldo: dinheiro(d.saldo_centavos, d.moeda) })}</p>}
        <div className="flex flex-col gap-3">
          <CampoData type="date" rotulo={t('w7.perdaData')} value={dataPerda} onChange={(e) => setDataPerda(e.target.value)} />
          <label className="flex flex-col gap-1.5 text-sm font-medium text-tinta-2">
            {t('w7.perdaMotivo')}
            <AreaSensivel rows={3} maxLength={300} value={motivoPerda} onChange={(e) => setMotivoPerda(e.target.value)} className="w-full rounded-lg border border-borda-campo bg-campo px-3 py-2 text-sm text-tinta-1 focus:border-foco focus:outline-2 focus:outline-offset-0 focus:outline-foco" />
          </label>
          {!motivoPerda.trim() && <p className="text-xs text-tinta-3">{t('w7.perdaMotivoObrigatorio')}</p>}
          {erroPerda && (
            <p role="alert" className="rounded-lg border border-perigo px-3 py-2 text-sm text-perigo">
              {erroPerda}
            </p>
          )}
        </div>
      </Modal>

      <Confirmar
        aberto={cobrando}
        aoFechar={() => setCobrando(false)}
        aoConfirmar={() => gerar.mutate()}
        carregando={gerar.isPending}
        titulo={t('cobranca.gerar')}
        texto={
          <div className="flex flex-col gap-3">
            <p>{t('cobranca.escolha')}</p>
            <div role="radiogroup" aria-label={t('cobranca.formaRotulo')} className="grid grid-cols-2 gap-2">
              {FORMAS_COBRANCA.map((f) => (
                <button
                  key={f}
                  type="button"
                  role="radio"
                  aria-checked={formaEscolhida === f}
                  onClick={() => setForma(f)}
                  className={`h-10 flex-1 rounded-lg border-2 text-sm font-medium ${formaEscolhida === f ? 'border-acento-forte bg-acento-suave text-tinta-1' : 'border-tinta-3 text-tinta-2 hover:bg-sup-2'}`}
                >
                  {t(`cobranca.forma.${f}`)}
                </button>
              ))}
            </div>
            <p className="text-xs text-tinta-3">{tp(`cobranca.formaAjuda.${formaEscolhida}`, provedorNovo)}</p>
          </div>
        }
      />
    </>
  );
}
